/**
 * BB-14d1/BB-14d2c2 -- client for the agent-media surface.
 *
 * Uses the shared `api` axios instance every other real-backed surface
 * uses, so `Authorization` and `X-Account-ID` are attached by its
 * existing request interceptor. This module never accepts or forwards a
 * caller-supplied account, connection, or VICIdial identifier -- the
 * endpoint takes no body precisely so a browser cannot nominate any of
 * them.
 *
 * **The response carries a SIP secret.** It is returned to the caller
 * and never touched again here: not stored, not cached, not logged, not
 * put on `window`, and never placed in a URL. `mediaLegClient` takes it
 * straight into memory. The only durable artifact this module may
 * produce is the non-secret metadata in `describeBootstrapForLog`.
 */

import { api } from '../lib/api';

export const BOOTSTRAP_PATH = '/telephony/agent-media/bootstrap';
export const READINESS_PATH = '/telephony/agent-media/readiness';
export const REVOKE_PATH = '/telephony/agent-media/revoke';

/**
 * Refusal codes the browser must distinguish. `already_active` means a
 * duplicate tab, `lease_not_owned` means native campaign login has not
 * happened yet -- categorically different remedies.
 */
export const BootstrapRefusal = Object.freeze({
  FEATURE_DISABLED: 'feature_disabled',
  NOT_CONFIGURED: 'not_configured',
  ALREADY_ACTIVE: 'already_active',
  LEASE_NOT_OWNED: 'lease_not_owned',
  CONNECTION_NOT_FOUND: 'connection_not_found',
  AGENT_IDENTITY_NOT_FOUND: 'agent_identity_not_found',
  AGENT_IDENTITY_CONNECTION_MISMATCH: 'agent_identity_connection_mismatch',
  MEDIA_NOT_CONFIGURED: 'media_not_configured',
});

/**
 * Request one ephemeral media identity for the authenticated agent.
 *
 * Resolves with the raw response payload -- including `sip_secret` when
 * `issued` is true. Callers must treat the result as memory-only.
 */
export async function bootstrapAgentMedia() {
  const response = await api.post(BOOTSTRAP_PATH, {});
  return response.data;
}

/**
 * The only representation of a bootstrap result that may be logged or
 * sent to telemetry.
 *
 * Built by naming safe fields explicitly rather than by deleting the
 * secret from a copy: a future response field would then be omitted by
 * default instead of leaking by default.
 */
export function describeBootstrapForLog(payload) {
  if (!payload || typeof payload !== 'object') {
    return { issued: false };
  }
  return {
    issued: Boolean(payload.issued),
    peer_name: payload.peer_name ?? null,
    expires_at: payload.expires_at ?? null,
    lease_generation: payload.lease_generation ?? null,
    sip_realm: payload.sip_realm ?? null,
    refusal: payload.refusal ?? null,
  };
}

/**
 * BB-14d2c2 -- `GET /api/telephony/agent-media/readiness`.
 *
 * Takes nothing, and that is the contract. Every identifier the
 * evaluator needs is derived from the authenticated session, so there
 * is no parameter here through which a browser could ask about another
 * agent's media.
 *
 * Returns the backend's own `{ready, reason}` unchanged. Callers must
 * treat a resolved promise as an answer, never as readiness: `ready` is
 * the only field that means ready.
 *
 * A 404 means `TELEPHONY_BB14_AGENT_MEDIA_API_ENABLED` is off. It is
 * normalised to a non-ready answer rather than thrown, because a
 * disabled surface is a configuration state, not an error, and must
 * present exactly as "audio is not available" -- never as a crash.
 */
export async function fetchAgentMediaReadiness() {
  try {
    const response = await api.get(READINESS_PATH);
    const data = response.data || {};
    return { ready: data.ready === true, reason: data.reason ?? 'not_ready' };
  } catch (error) {
    if (error?.response?.status === 404) {
      return { ready: false, reason: 'not_ready' };
    }
    throw error;
  }
}

/**
 * BB-14d2c2 -- `POST /api/telephony/agent-media/revoke`.
 *
 * **`peerName` is a selector, not authority.** It names *which* of this
 * authenticated session's identities to release; the account, provider
 * connection and VICIdial user are all derived server-side. A stale tab
 * presenting a peer that has since been superseded gets
 * `revoked: false` and cannot touch its replacement.
 *
 * There is deliberately no "revoke whatever is current" form of this
 * function. The caller must name the exact peer it was issued, which is
 * what makes it impossible for a controller being torn down to revoke a
 * controller that replaced it.
 *
 * Best-effort and convergent: resolves `false` rather than throwing on
 * any failure, because a failed cleanup must never propagate into a
 * native state transition. BB-6L campaign logout remains the
 * authoritative server-side cleanup either way.
 */
export async function revokeAgentMedia(peerName) {
  if (typeof peerName !== 'string' || peerName.length === 0) {
    return false;
  }
  try {
    const response = await api.post(REVOKE_PATH, { peer_name: peerName });
    return response?.data?.revoked === true;
  } catch (error) {
    return false;
  }
}
